/**
 * Lion dance drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lunar_new_year`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const lionDanceDrawingsPack: ToilePack = {
    slug: 'lion-dance-drawings',
    category: 'season',
    name: 'Lion dance drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'head', label: 'Lion head', subject: 'a southern lion head seen from the front, the single horn, the big eyes with lashes, the mirror on the forehead and the open mouth with a fringe of fur'},
        {key: 'leap', label: 'Leaping lion', subject: 'a full southern lion costume rearing up mid-dance, the long tail cloth swirling behind, fur tufts along the back'},
        {key: 'lettuce', label: 'Lion and lettuce', subject: 'a lion head reaching up for a hanging lettuce tied to a red envelope, the cai qing moment, firecracker smoke curling below'},
    ],
    styles: [
        {key: 'outline', label: 'Clean outline', style: 'clean uniform black outline with no fills, ready to color or trace', palette: 'black ink only', framing: 'The lion fills the square, centered with a white margin.'},
        {key: 'ink', label: 'Brush ink', style: 'expressive Chinese brush ink drawing, tapered strokes, solid black fur masses and dry-brush texture', palette: 'black ink only', framing: 'The lion is centered with a white margin and loose ink splashes around it.'},
        {key: 'poster', label: 'Mural block', style: 'bold simplified mural linework, thick black shapes and flat black fills for fur and eyes, readable from a distance', palette: 'black ink only', framing: 'The lion fills the square, centered with a white margin.'},
    ],
    directions: ['white lion', 'golden lion', 'northern lion'],
    directionHint: 'try: white lion',
    seo: {
        title: 'Lion Dance Drawings: Head, Leaping Lion, Lettuce',
        metaDescription: 'Lion dance drawings to copy, colour or trace: a lion head, a leaping lion and a lion reaching for lettuce, in three drawing styles.',
        h1: 'Lion dance drawings: lion head, leaping lion, lion and lettuce',
        tagline: 'Nine lion pictures with no dancer, in three drawing styles: clean outline, brush ink and mural block.',
        intro: 'Drawings of the lion of the Lunar New Year lion dance, to copy, colour or trace onto a poster, a mural or a class project. Each picture shows the lion alone: a southern lion head with its single horn and mirror, a full costume rearing up with its tail cloth swirling, or a lion reaching up for a lettuce tied to a red envelope. Type a different lion, such as a white lion, and the same drawing styles carry it.',
        useCases: [
            'A poster for a school New Year event',
            'A mural for a community hall',
            'A colouring or tracing page for a class',
            'A drawing practice sheet',
        ],
        faq: [
            {q: 'Is there a dancer in the pictures?', a: 'No. The pictures show the lion alone, with no dancer or person visible.'},
            {q: 'Can I trace these?', a: 'Yes. The clean outline style has no fills and is ready to colour or trace.'},
            {q: 'What is the difference between the three styles?', a: 'Clean outline is a uniform black line, brush ink has tapered strokes and solid black fur, and mural block has thick black shapes that read from a distance.'},
            {q: 'Can I change the lion?', a: 'Yes. Type a different lion, such as a golden lion; the result may differ from the sample.'},
        ],
    },
}
